import React from 'react';
import {
    Grid,
    makeStyles,
    useMediaQuery,
    useTheme,
    Typography,
    Box,
    Button,
} from '@material-ui/core';
import Graphic from '../../objects/graphicsLibrary/graphic';
import useSorting, { Sort } from '../../shared/hooks/useSorting';
import usePagination from '../../shared/hooks/usePagination';
import Graphics from '../../objects/graphicsLibrary/graphics';
import { useDispatch } from 'react-redux';
import {
    CustomFilterItemValue,
    PredefinedViewValue,
} from '../../shared/components/filter/types';
import { ImperativeHandle } from '../../shared/components/filter/Filter';
import PaginationContainer from '../../shared/components/pagination/PaginationContainer';
import Pagination from '../../shared/components/pagination/Pagination';
import GraphicsFilter from './GraphicsFilter';
import * as actions from '../../store/actions';
import GraphicsSorter from './GraphicsSorter';
import GraphicDetail from './GraphicDetail/GraphicDetail';
import ListItem from './ListItem';
import { useTranslation } from 'react-i18next';

export type DataFetcher = (
    filter: { [name: string]: number | string | boolean | null },
    page: number,
    pageSize: number,
    sort: Sort[] | null
) => void;

interface Props {
    defaultSort?: Sort;
    dataFetcher: DataFetcher;
    data: {
        graphics: Graphics | null;
        loadCounter: number;
        loadFailed: boolean;
        allCount: number;
        customFilter: CustomFilterItemValue[] | null;
    };
    onInsertGraphicFuncName: string;
    currentDesignId: string;
    customerId: number | null;
    guestId: number | null;
    showGraphicUpload: () => void;
}

const useStyles = makeStyles((theme) => ({
    list: {
        minHeight: '350px',
    },
    infoTextWrap: {
        width: '100%',
        textAlign: 'center',
    },
    infoText: {
        textAlign: 'center',
        textTransform: 'uppercase',
        padding: '100px 0px',
    },
    titleButton: {
        height: '2rem',
        marginRight: '1rem',
        marginTop: '-2px',
    },
}));

const List: React.FC<Props> = ({
    defaultSort,
    data,
    dataFetcher,
    onInsertGraphicFuncName,
    currentDesignId,
    customerId,
    guestId,
    showGraphicUpload,
}) => {
    const [activeFilter, setActiveFilter] = React.useState<
        PredefinedViewValue[] | null
    >();
    const [activeFilterHistory, setActiveFilterHistory] = React.useState<any[]>(
        []
    );
    const [showFilter, setShowFilter] = React.useState<boolean>(true);
    const classes = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const filterRef = React.createRef<ImperativeHandle>();
    const [selectedGraphic, setSelectedGraphic] =
        React.useState<Graphic | null>();

    const theme = useTheme();
    const downSm = useMediaQuery(theme.breakpoints.down('sm'));
    const downLg = useMediaQuery(theme.breakpoints.down('lg'));

    const itemsToDisplay = downSm ? 15 : downLg ? 30 : 45;

    const { sort, toggleSort } = useSorting(defaultSort);
    const { itemsPerPage, ...paginationProps } = usePagination({
        itemsCount: data.allCount,
        itemsPerPage: itemsToDisplay,
    });

    const { page, onGotoPage } = paginationProps;

    const loadData = (): void => {
        const filterForApi: {
            [name: string]: string | number | boolean | null;
        } = {};

        if (!activeFilter || activeFilter.length === 0) {
            filterForApi['all'] = true;
        } else {
            activeFilter.forEach((item: PredefinedViewValue) => {
                filterForApi[item.key] = item.value;
            });
        }
        setActiveFilterHistory([...activeFilterHistory, ...[filterForApi]]);
        setShowFilter(true);
        dataFetcher(filterForApi, page - 1, itemsPerPage, sort ? [sort] : null);
    };

    const [previousFilter, setPreviousFilter] = React.useState<any | null>();
    const [previousSort, setPreviousSort] = React.useState<any | null>();
    const [previousPage, setPreviousPage] = React.useState<any | null>();
    React.useEffect(() => {
        if (activeFilter !== undefined) {
            if (
                JSON.stringify(previousFilter) !==
                    JSON.stringify(activeFilter) ||
                previousSort !== sort ||
                previousPage !== page
            ) {
                loadData();
                setPreviousFilter(activeFilter);
                setPreviousSort(sort);
                setPreviousPage(page);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort, activeFilter, page]);

    React.useEffect(() => {
        if (
            activeFilterHistory.length === 1 &&
            data.graphics?.getItems().length === 0 &&
            activeFilter !== undefined &&
            activeFilter !== null &&
            activeFilter.length === 1
        ) {
            setActiveFilter(null);
            dispatch(actions.setGraphicsFilter(null));
            setShowFilter(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.graphics]);

    const handleShowGraphicDetail = (graphic: Graphic): void => {
        setSelectedGraphic(graphic);
    };

    const handleCloseGraphicDetail = (): void => {
        setSelectedGraphic(null);
    };

    const handleOnDelete = (): void => {
        setSelectedGraphic(null);
        loadData();
    };

    const areResults = !!data.allCount;

    return (
        <>
            {selectedGraphic && (
                <GraphicDetail
                    graphic={selectedGraphic}
                    handleOnClose={handleCloseGraphicDetail}
                    handleOnDelete={handleOnDelete}
                    onInsertGraphicFuncName={onInsertGraphicFuncName}
                    currentDesignId={currentDesignId}
                    customerId={customerId}
                    guestId={guestId}
                />
            )}
            <Grid container>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Grid container alignItems={'center'}>
                            <Grid item>
                                <Button
                                    onClick={showGraphicUpload}
                                    color="secondary"
                                    variant="outlined"
                                    className={classes.titleButton}
                                >
                                    {t('Upload')}
                                </Button>
                            </Grid>
                            <Grid item>
                                {showFilter && (
                                    <GraphicsFilter
                                        persistFilter={(
                                            customFilterParam:
                                                | CustomFilterItemValue[]
                                                | null,
                                            predefinedViewFilterParam:
                                                | string[]
                                                | null
                                        ): void => {
                                            dispatch(
                                                actions.setGraphicsFilter(
                                                    customFilterParam
                                                )
                                            );
                                        }}
                                        ref={filterRef}
                                        onChange={(filter): void => {
                                            setActiveFilter(filter);
                                            onGotoPage(1);
                                        }}
                                        defaultCustomFilterItems={
                                            data.customFilter || undefined
                                        }
                                        currentDesignId={currentDesignId}
                                        customerId={customerId}
                                        guestId={guestId}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <GraphicsSorter sort={sort} toggleSort={toggleSort} />
                    </Grid>
                </Grid>
                <Grid
                    container
                    className={classes.list}
                    justifyContent="flex-start"
                    alignContent="flex-start"
                >
                    {data.loadFailed && (
                        <Box className={classes.infoTextWrap}>
                            <Typography className={classes.infoText}>
                                {t(
                                    'Something went wrong. Try to change, or delete your active filters.'
                                )}
                            </Typography>
                        </Box>
                    )}
                    {!data.loadFailed && (
                        <>
                            {!data.loadCounter &&
                                data.graphics &&
                                data.graphics.getItems().length === 0 && (
                                    <Box className={classes.infoTextWrap}>
                                        <Typography
                                            className={classes.infoText}
                                        >
                                            {t(
                                                'There are no graphics, according to your filter.'
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                            {!data.loadCounter &&
                                data.graphics &&
                                data.graphics
                                    .getItems()
                                    .map((item: Graphic, index) => (
                                        <Grid item key={'item-' + item.getId()}>
                                            <ListItem
                                                item={item}
                                                handleShowGraphicDetail={
                                                    handleShowGraphicDetail
                                                }
                                                index={item.getId()}
                                            />
                                        </Grid>
                                    ))}
                            {data.loadCounter > 0 &&
                                Array.from(new Array(3)).map(
                                    (item: Graphic, index) => (
                                        <Grid item key={'item-' + index}>
                                            <ListItem
                                                item={item}
                                                handleShowGraphicDetail={
                                                    handleShowGraphicDetail
                                                }
                                                index={index}
                                            />
                                        </Grid>
                                    )
                                )}
                        </>
                    )}
                </Grid>
            </Grid>
            <PaginationContainer>
                {areResults && (
                    <Pagination
                        itemsCount={data.allCount}
                        {...paginationProps}
                    />
                )}
            </PaginationContainer>
        </>
    );
};

export default List;
